import { Injectable, DOCUMENT, inject } from '@angular/core'
import { JwtHelperService } from '@auth0/angular-jwt'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import { Apollo } from 'apollo-angular'
import { DecodedToken } from '@wm-front/shared/interfaces'
import { StateResetAll } from 'ngxs-reset-plugin'
import { LoginFromStorage, Logout, SetToken } from './auth.actions'
import { UserRole } from '@wm-front/shared/enums'
import { Router } from '@angular/router'
import { CubejsService, SegmentService } from '@wm-front/core'
import { Environment, ENVIRONMENT } from '@wm-front/shared/environment'
import { ClerkService } from '@wm-front/clerk'
import { FeatureFlagService } from '@wm-front/core-tools'
import { toSignal } from '@angular/core/rxjs-interop'

export interface AuthStateModel {
  accessToken: string
  decodedToken: DecodedToken | null
  error: Error | null
  loading: boolean
  mode: 'legacy' | 'clerk'
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    accessToken: '',
    mode: 'legacy',
    decodedToken: {
      id: 0,
      email: '',
      iat: 0,
      exp: 0,
      payload: {
        adminId: 0,
        role: UserRole.Admin,
      },
    },
    error: null,
    loading: false,
  },
})
@Injectable()
export class AuthState {
  /** The name of the token as stored in the storage */
  public tokenName = 'wm-admin-token'

  protected _document = inject<Document>(DOCUMENT)

  protected env = inject<Environment>(ENVIRONMENT)

  private _store = inject(Store)

  private _router = inject(Router)

  private _apollo = inject(Apollo)

  private _cubejs = inject(CubejsService)

  private _segmentService = inject(SegmentService)

  private _featureFlagService = inject(FeatureFlagService)

  private shouldNotBeAnonymous = toSignal(
    this._featureFlagService.watch('deactivate-anonymity-on-the-admin', false)
  )

  private _clerk = inject(ClerkService)

  /** The jwt helper service provided by the @auth/angular-jwt package */
  private jwtHelper = new JwtHelperService()

  /** The window object */
  private window: Window

  constructor() {
    const _document = this._document
    this.window = _document.defaultView as Window
  }

  /**  Selects whether or not the user has connected
   * @param { AuthStateModel } state the state model
   * @return { boolean }
   */
  @Selector()
  public static isAuthenticated(state: AuthStateModel): boolean {
    const jwtHelper = new JwtHelperService()
    return !jwtHelper.isTokenExpired(state.accessToken)
  }

  /** Select Auth access token
   * @param { AuthStateModel } state the state model
   * @return { string }
   */
  @Selector()
  public static accessToken(state: AuthStateModel): string {
    return state?.accessToken
  }

  /** Select Auth decoded token
   * @param { AuthStateModel } state the state model
   * @return { DecodedToken }
   */
  @Selector()
  public static decodedToken(state: AuthStateModel): DecodedToken | null {
    return state?.decodedToken
  }

  /** Selects the username of the logged in user
   * @param { AuthStateModel } state the state model
   * @return { string }
   */
  @Selector()
  public static username(state: AuthStateModel): string {
    return state?.decodedToken?.name as string
  }

  /** Set the token returned from the google auth in the store and in the storage
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @return { Promise<AuthStateModel | undefined> }
   */
  @Action(SetToken)
  public setToken(
    ctx: StateContext<AuthStateModel>,
    { token, mode }: SetToken
  ): void {
    if (token) {
      this.setTokenInStorage(token)

      ctx.patchState({
        accessToken: token,
        mode,
        decodedToken: this.jwtHelper.decodeToken(token),
        error: null,
      })
    }
  }

  /** Set the login state from localStorage
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @param { Login } payload the tab value used to patch the state
   * @return { AuthStateModel | void }
   */
  @Action(LoginFromStorage)
  public loginFromStorage(
    ctx: StateContext<AuthStateModel>
  ): AuthStateModel | void {
    const tokenStorage = this.window.localStorage.getItem(this.tokenName)
    const mode = this.window.localStorage.getItem('auth-mode') || 'legacy'

    if (mode === 'clerk') return // we don't handle clerk login from storage in this state

    if (tokenStorage) {
      const state = ctx.getState()
      const accessToken = state.accessToken
      const jwtHelper = new JwtHelperService()
      const isTokenExpired = jwtHelper.isTokenExpired(tokenStorage)
      const decodedToken = this.jwtHelper.decodeToken(tokenStorage)

      if (!isTokenExpired && !accessToken) {
        this._segmentService.identify(
          decodedToken.id,
          {
            email: decodedToken.email,
          },
          undefined,
          this.shouldNotBeAnonymous()
        )
        this._cubejs.init(tokenStorage, [
          {
            version: 'cubi',
            apiUrl: this.env.cubejsV2ApiUrl,
          },
        ])

        return ctx.patchState({
          accessToken: tokenStorage,
          decodedToken,
        })
      }
    }
  }

  /** Reset the login from localStorage
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @return { AuthStateModel }
   */
  @Action(Logout)
  public async logout(): Promise<void> {
    this.removeTokenInStorage()
    this._clerk.signOut()
    this._store.dispatch(new StateResetAll())
    await this._apollo.client.resetStore()
    await this._router.navigate(['/login'])
  }

  /** Set token in local storage
   * @param { string } accessToken
   * @return { void }
   */
  private setTokenInStorage(accessToken: string): void {
    this.window.localStorage.setItem(this.tokenName, accessToken)
  }

  /** Remove token in local storage
   * @return { void }
   */
  private removeTokenInStorage(): void {
    this.window.localStorage.removeItem(this.tokenName)
  }
}
